import { useContext } from 'react'
import { HomeContext } from '../../../state/home-context'
import WidgetLoading from '../../../../../components/common/cards/widget-loading'
import WidgetErrorCard from '../../../../../components/common/cards/widget-error-card'
import MultipleXAxisBarChart from '../../multiple-xaxis-bar-chart/multiple-xaxis-bar-chart'
import { useApiGetWeekGmvForecastGapIndicators } from 'core/api/home/weekly/useApiGetWeekGmvForecastGapIndicators'
import GmvForecastGapSummary from './gmv-forecast-gap-summary'
import GmvForecastGapLegendEntry from './gmv-forecast-gap-legend-entry'
import GmvForecastGapTitle from './gmv-forecast-gap-title'
import { formatFloatDecimal } from 'core/utils/number'

interface WeeklyGmvForecastGapProps {
  yearWeek: string
}

const WeeklyGmvForecastGap = ({ yearWeek }: WeeklyGmvForecastGapProps) => {
  const { channel, storeId, store } = useContext(HomeContext)
  const currency = store?.currency ?? ''
  const { data, isLoading } = useApiGetWeekGmvForecastGapIndicators({ storeId, channel, week: yearWeek }, !!storeId)

  if (isLoading) {
    return (
      <WidgetLoading
        className="mg-weekly-gmv-gap mg-weekly-gmv-gap-loading"
        dataTestId="weekly-gmv-forecast-gap-loading"
        skeletonCount={3}
      />
    )
  }

  const directorGmv = formatFloatDecimal(data?.directorGmv, 1)
  const teamsGmv = formatFloatDecimal(data?.teamsGmv, 1)
  const trajectoryGmv = formatFloatDecimal(data?.trajectoryGmv, 1)

  if (directorGmv == null && teamsGmv == null && trajectoryGmv == null) {
    return (
      <WidgetErrorCard
        titleKey="general.homepage.weekly-gmv-forecast-gap.title"
        messageKey="general.homepage.unavailable"
        testId="weekly-gmv-forecast-gap-unavailable"
      />
    )
  }

  return (
    <div className="mg-weekly-gmv-gap">
      <GmvForecastGapTitle />
      <div className="mg-weekly-gmv-gap-content">
        <div className="mg-weekly-gmv-gap-summary-chart">
          <GmvForecastGapSummary directorGmv={directorGmv} teamsGmv={teamsGmv} currency={currency} />
          <MultipleXAxisBarChart data1={directorGmv} data2={teamsGmv} dataBackground={trajectoryGmv} />
        </div>

        <div className="mg-weekly-gmv-gap-chart-legend">
          <GmvForecastGapLegendEntry
            labelKey="general.homepage.weekly-gmv-forecast-gap.legend.store-manager"
            value={directorGmv}
            comparisonBaseValue={trajectoryGmv}
            currency={currency}
            iconColor="var(--vp-semantic-color-content-brand)"
          />
          <GmvForecastGapLegendEntry
            labelKey="general.homepage.weekly-gmv-forecast-gap.legend.teams"
            value={teamsGmv}
            comparisonBaseValue={trajectoryGmv}
            currency={currency}
            iconColor="var(--vp-semantic-color-extended-turquoise)"
          />
          <GmvForecastGapLegendEntry
            labelKey="general.homepage.weekly-gmv-forecast-gap.legend.trajectory"
            value={trajectoryGmv}
            currency={currency}
            iconColor="var(--vp-semantic-color-status-info-quiet)"
            iconBorder="0.75px dashed var(--vp-semantic-color-content-brand)"
          />
        </div>
      </div>
    </div>
  )
}

export default WeeklyGmvForecastGap
